
import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/auth';
import { supabase } from '@/integrations/supabase/client';

interface ProtectedRouteProps {
  children: React.ReactNode;
  public?: boolean; // New prop to allow public access but with limited functionality
  requiresAuth?: boolean; // If true, requires authentication regardless of public status
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  public: isPublic = false,
  requiresAuth = false
}) => {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  const [isSessionValid, setIsSessionValid] = useState<boolean | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  // Verify session is valid on protected routes
  useEffect(() => {
    const validateSession = async () => {
      if (!user || isPublic) return;
      
      setIsValidating(true);
      try {
        // Check if session is valid by getting user
        const { data: { user: currentUser } } = await supabase.auth.getUser();
        setIsSessionValid(!!currentUser);
      } catch (error) {
        console.error("Session validation error:", error);
        setIsSessionValid(false);
      } finally {
        setIsValidating(true);
      }
    };
    
    validateSession();
  }, [user, isPublic]);

  // If authentication is still loading, show a loading indicator
  if (isLoading || isValidating) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  // If the route is public and doesn't require auth, allow access even if not authenticated
  if (isPublic && !requiresAuth) {
    return <>{children}</>;
  }

  // If the user is not authenticated or session is invalid, redirect to the login page
  if (!user || isSessionValid === false) {
    // Save the current location for redirecting back after login
    return <Navigate to="/login" state={{ returnTo: location.pathname }} replace />;
  }

  // If the user is authenticated and session is valid, render the children
  return <>{children}</>;
};

export default ProtectedRoute;
